import { Fragment, useId, useRef, useState, type ReactNode } from "react";
import { useSession } from "../hooks";
import type { Message } from "../sync/types";
import { t } from "../i18n";
import { useInvalid } from "./ui";

// Block Kit as Slack apps send it (ADR-0008): the layout blocks people see
// most, and buttons and selects, which send block actions to the app.

interface TextObj {
  type: "plain_text" | "mrkdwn";
  text: string;
}

interface Element {
  type: string;
  action_id?: string;
  value?: string;
  style?: "primary" | "danger";
  text?: TextObj;
  url?: string;
  image_url?: string;
  alt_text?: string;
  placeholder?: TextObj;
  options?: { text: TextObj; value: string }[];
  multiline?: boolean;
  initial_value?: string;
  initial_option?: { value: string };
  initial_options?: { value: string }[];
  initial_date?: string;
  initial_user?: string;
  initial_conversation?: string;
  min_length?: number;
  max_length?: number;
  min_value?: string;
  max_value?: string;
  is_decimal_allowed?: boolean;
}

export interface Block {
  type: string;
  block_id?: string;
  label?: TextObj;
  hint?: TextObj;
  optional?: boolean;
  element?: Element;
  text?: TextObj;
  fields?: TextObj[];
  accessory?: Element;
  elements?: (Element | TextObj)[];
  image_url?: string;
  alt_text?: string;
  title?: TextObj;
}

const slackUser = /^[UW]([0-9A-F]{32})$/;

function uuidOf(hex: string) {
  const h = hex.toLowerCase();
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

function unescape(s: string) {
  return s.replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&amp;/g, "&");
}

const mrkdwnToken = /```([\s\S]+?)```|`([^`\n]+)`|\*([^*\n]+)\*|_([^_\n]+)_|~([^~\n]+)~|<([^>|]+)(?:\|([^>]+))?>/g;

// Mrkdwn renders Slack's mrkdwn: *bold*, _italic_, ~strike~, `code`,
// ```blocks```, <links|labels> and <@user> mentions. Nothing else is
// interpreted, and no HTML.
export function Mrkdwn({ text, name }: { text: string; name: (id: string) => string }) {
  const out: ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(mrkdwnToken)) {
    if (m.index > last) out.push(unescape(text.slice(last, m.index)));
    const k = m.index;
    if (m[1] !== undefined) out.push(<pre key={k}>{unescape(m[1])}</pre>);
    else if (m[2] !== undefined) out.push(<code key={k}>{unescape(m[2])}</code>);
    else if (m[3] !== undefined) out.push(<strong key={k}>{unescape(m[3])}</strong>);
    else if (m[4] !== undefined) out.push(<em key={k}>{unescape(m[4])}</em>);
    else if (m[5] !== undefined) out.push(<s key={k}>{unescape(m[5])}</s>);
    else {
      const target = m[6];
      const user = target.startsWith("@") ? target.slice(1) : "";
      if (user) {
        const id = slackUser.test(user) ? uuidOf(user.slice(1)) : user;
        out.push(
          <span key={k} className="mention">
            @{name(id)}
          </span>,
        );
      } else if (/^https?:\/\//.test(target)) {
        out.push(
          <a key={k} href={target} target="_blank" rel="noopener noreferrer">
            {unescape(m[7] ?? target)}
          </a>,
        );
      } else {
        out.push(unescape(m[7] ?? target));
      }
    }
    last = k + m[0].length;
  }
  out.push(unescape(text.slice(last)));
  return (
    <>
      {out.map((part, i) =>
        typeof part === "string"
          ? part.split("\n").map((line, j) => (
              <Fragment key={`${i}-${j}`}>
                {j > 0 && <br />}
                {line}
              </Fragment>
            ))
          : part,
      )}
    </>
  );
}

// Images from other hosts are not loaded (the client's CSP keeps it to its
// own origin, for air-gapped sites); they show as links.
function Img({ url, alt }: { url?: string; alt?: string }) {
  if (!url) return null;
  if (url.startsWith("/") || url.startsWith("data:")) return <img src={url} alt={alt ?? ""} className="block-image" />;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="block-image-link">
      {t("Image: {name}", { name: alt || url })}
    </a>
  );
}

export function Blocks({ m }: { m: Message }) {
  const session = useSession();
  const blocks = ((m.payload as { blocks?: Block[] } | null)?.blocks ?? []).slice(0, 50);
  return (
    <BlockList
      blocks={blocks}
      onAction={(blockID, actionID, value) => session.api.blockAction(m.channel_id, m.id, { block_id: blockID, action_id: actionID, value })}
    />
  );
}

// InputValue is what a person entered in an input block: text, an option's
// value, an ID, or a list of them for multi-selects and checkboxes.
export type InputValue = string | string[];

export interface Inputs {
  values: Record<string, Record<string, InputValue>>;
  set: (blockID: string, actionID: string, v: InputValue) => void;
  errors: Record<string, string>;
}

// BlockList renders blocks; actions go to onAction. With inputs, as in a
// modal, input blocks are form fields.
export function BlockList({
  blocks,
  onAction,
  inputs,
}: {
  blocks: Block[];
  onAction: (blockID: string, actionID: string, value: string) => Promise<unknown>;
  inputs?: Inputs;
}) {
  const session = useSession();
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const name = (id: string) => session.name(id);
  const txt = (o?: TextObj) => (o ? o.type === "mrkdwn" ? <Mrkdwn text={o.text} name={name} /> : o.text : null);

  async function act(blockID: string | undefined, el: Element, value?: string) {
    if (!el.action_id) return;
    setBusy(el.action_id);
    setError("");
    try {
      await onAction(blockID ?? "", el.action_id, value ?? el.value ?? "");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy("");
    }
  }

  const element = (b: Block, el: Element, i: number) => {
    switch (el.type) {
      case "button":
        if (el.url) {
          return (
            <a key={i} className={`block-button ${el.style ?? ""}`} href={el.url} target="_blank" rel="noopener noreferrer">
              {el.text?.text}
            </a>
          );
        }
        return (
          <button key={i} className={`block-button ${el.style ?? ""}`} disabled={busy === el.action_id} onClick={() => act(b.block_id, el)}>
            {el.text?.text}
          </button>
        );
      case "static_select":
        return (
          <select key={i} aria-label={el.placeholder?.text ?? t("Choose")} defaultValue="" onChange={(e) => act(b.block_id, el, e.target.value)}>
            <option value="" disabled>
              {el.placeholder?.text ?? t("Choose…")}
            </option>
            {el.options?.map((o) => (
              <option key={o.value} value={o.value}>
                {o.text.text}
              </option>
            ))}
          </select>
        );
      case "image":
        return <Img key={i} url={el.image_url} alt={el.alt_text} />;
      case "mrkdwn":
      case "plain_text":
        return <span key={i}>{txt(el as unknown as TextObj)}</span>;
    }
    return null;
  };

  return (
    <div className="blocks">
      {blocks.map((b, i) => {
        switch (b.type) {
          case "header":
            return (
              <h3 key={i} className="block-header">
                {b.text?.text}
              </h3>
            );
          case "divider":
            return <hr key={i} />;
          case "section":
            return (
              <div key={i} className="block-section">
                <div className="block-body">
                  {b.text && <div>{txt(b.text)}</div>}
                  {b.fields && (
                    <div className="block-fields">
                      {b.fields.map((f, j) => (
                        <div key={j}>{txt(f)}</div>
                      ))}
                    </div>
                  )}
                </div>
                {b.accessory && <div className="block-accessory">{element(b, b.accessory, 0)}</div>}
              </div>
            );
          case "context":
            return (
              <div key={i} className="block-context">
                {b.elements?.map((el, j) => element(b, el as Element, j))}
              </div>
            );
          case "image":
            return <Img key={i} url={b.image_url} alt={b.alt_text ?? b.title?.text} />;
          case "actions":
            return (
              <div key={i} className="block-actions">
                {b.elements?.map((el, j) => element(b, el as Element, j))}
              </div>
            );
          case "input":
            if (!inputs || !b.element) return null;
            return <InputBlock key={i} b={b} inputs={inputs} />;
        }
        return null; // a block this client does not know
      })}
      {error && <p className="error">{error}</p>}
    </div>
  );
}

// initialValue is an input element's starting value.
export function initialValue(el: Element): InputValue {
  if (el.type === "multi_static_select" || el.type === "checkboxes") return (el.initial_options ?? []).map((o) => o.value);
  return el.initial_value ?? el.initial_option?.value ?? el.initial_date ?? el.initial_user ?? el.initial_conversation ?? "";
}

function InputBlock({ b, inputs }: { b: Block; inputs: Inputs }) {
  const session = useSession();
  const el = b.element!;
  const blockID = b.block_id ?? "";
  const actionID = el.action_id ?? "";
  const value = inputs.values[blockID]?.[actionID] ?? initialValue(el);
  const set = (v: InputValue) => inputs.set(blockID, actionID, v);
  const id = `in-${blockID}-${actionID}`;
  const one = typeof value === "string" ? value : "";
  const many = Array.isArray(value) ? value : [];
  // What must be filled in, and how long or large, is checked before the
  // app is asked, as Slack's client does; what is wrong shows under it.
  const box = useRef<HTMLDivElement>(null);
  const errorID = useId();
  const said = useInvalid(box, errorID, inputs.errors[blockID]);
  const required = !b.optional;
  let field: ReactNode;
  switch (el.type) {
    case "plain_text_input":
      field = el.multiline ? (
        <textarea id={id} value={one} required={required} minLength={el.min_length} maxLength={el.max_length} placeholder={el.placeholder?.text} onChange={(e) => set(e.target.value)} />
      ) : (
        <input id={id} value={one} required={required} minLength={el.min_length} maxLength={el.max_length} placeholder={el.placeholder?.text} onChange={(e) => set(e.target.value)} />
      );
      break;
    case "email_text_input":
    case "url_text_input":
    case "number_input":
      field = (
        <input
          id={id}
          type={el.type === "email_text_input" ? "email" : el.type === "url_text_input" ? "url" : "number"}
          value={one}
          required={required}
          min={el.min_value}
          max={el.max_value}
          step={el.type === "number_input" ? (el.is_decimal_allowed ? "any" : 1) : undefined}
          placeholder={el.placeholder?.text}
          onChange={(e) => set(e.target.value)}
        />
      );
      break;
    case "datepicker":
      field = <input id={id} type="date" value={one} required={required} onChange={(e) => set(e.target.value)} />;
      break;
    case "static_select":
      field = (
        <select id={id} value={one} required={required} onChange={(e) => set(e.target.value)}>
          <option value="">{el.placeholder?.text ?? t("Choose…")}</option>
          {el.options?.map((o) => (
            <option key={o.value} value={o.value}>
              {o.text.text}
            </option>
          ))}
        </select>
      );
      break;
    case "multi_static_select":
      field = (
        <select id={id} multiple value={many} onChange={(e) => set([...e.target.selectedOptions].map((o) => o.value))}>
          {el.options?.map((o) => (
            <option key={o.value} value={o.value}>
              {o.text.text}
            </option>
          ))}
        </select>
      );
      break;
    case "users_select":
      field = (
        <select id={id} value={one} required={required} onChange={(e) => set(e.target.value)}>
          <option value="">{el.placeholder?.text ?? t("Choose someone…")}</option>
          {[...session.directory.values()]
            .filter((p) => p.kind === "user" && p.status === "active")
            .map((p) => (
              <option key={p.id} value={p.id}>
                {p.display_name}
              </option>
            ))}
        </select>
      );
      break;
    case "conversations_select":
    case "channels_select":
      field = (
        <select id={id} value={one} required={required} onChange={(e) => set(e.target.value)}>
          <option value="">{el.placeholder?.text ?? t("Choose a channel…")}</option>
          {[...session.store.getSnapshot().channels.keys()].map((c) => (
            <option key={c} value={c}>
              {session.channelName(c)}
            </option>
          ))}
        </select>
      );
      break;
    case "checkboxes":
    case "radio_buttons":
      field = (
        <div id={id} className="block-choices">
          {el.options?.map((o) => (
            <label key={o.value}>
              <input
                type={el.type === "checkboxes" ? "checkbox" : "radio"}
                name={id}
                required={required && el.type === "radio_buttons"}
                checked={el.type === "checkboxes" ? many.includes(o.value) : one === o.value}
                onChange={(e) =>
                  el.type === "checkboxes" ? set(e.target.checked ? [...many, o.value] : many.filter((v) => v !== o.value)) : set(o.value)
                }
              />{" "}
              {o.text.text}
            </label>
          ))}
        </div>
      );
      break;
    default:
      field = <span className="muted">{t("This field cannot be shown here.")}</span>;
  }
  return (
    <div ref={box} className="block-input">
      <label htmlFor={id}>
        {b.label?.text}
        {b.optional && <span className="muted"> {t("(optional)")}</span>}
      </label>
      {field}
      {b.hint && <div className="hint">{b.hint.text}</div>}
      {said && (
        <span className="field-error" id={errorID}>
          {said}
        </span>
      )}
    </div>
  );
}
